import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAdminFile, AdminFileItem, deleteAdminFile } from '../services/adminApi';

const getPreview = (file: AdminFileItem) => {
  if (file.contentType.startsWith('image/')) {
    return <img src={file.url} alt={file.fileName} style={{ maxWidth: '100%', borderRadius: '1rem' }} />;
  }

  if (file.contentType.startsWith('video/')) {
    return (
      <video controls style={{ maxWidth: '100%', borderRadius: '1rem' }}>
        <source src={file.url} type={file.contentType} />
        Your browser does not support the video tag.
      </video>
    );
  }

  return null;
};

const FileDetailsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [file, setFile] = useState<AdminFileItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!id) {
      setError(t('pages.filesDetailsNotFound', 'File not found.'));
      setLoading(false);
      return;
    }

    const loadFile = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getAdminFile(id);
        setFile(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : t('pages.filesDetailsError', 'Unable to load file details.'));
      } finally {
        setLoading(false);
      }
    };

    void loadFile();
  }, [id, t]);

  const handleDelete = async () => {
    if (!file || !id || !confirm(t('pages.filesDeleteConfirm', 'Delete this file?'))) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await deleteAdminFile(id);
      navigate('/admin/files');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('pages.filesDeleteFailed', 'Delete failed.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-grid">
      <div className="section-heading">
        <h2>{t('pages.filesDetailsTitle', 'File details')}</h2>
        <p>{t('pages.filesDescription', 'Upload images, videos, and documents to Firestore storage and view uploaded files.')}</p>
      </div>

      {loading ? (
        <div>{t('pages.filesDetailsLoading', 'Loading file details...')}</div>
      ) : error ? (
        <div className="alert alert-error">{error}</div>
      ) : file ? (
        <div className="admin-panel">
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ margin: 0 }}>{file.fileName}</h3>
                <p className="muted" style={{ margin: '0.5rem 0 0' }}>
                  {t('pages.filesDetailsContentType', 'Content type')}: {file.contentType}
                </p>
                <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                  {t('pages.filesDetailsSize', 'Size')}: {`${(file.size / 1024).toFixed(1)} KB`}
                </p>
                <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                  {t('pages.filesDetailsUploadedAt', 'Uploaded at')}: {new Date(file.uploadedAt).toLocaleString()}
                </p>
              </div>
              <div className="admin-actions" style={{ alignItems: 'center' }}>
                <a href={file.url} target="_blank" rel="noreferrer" className="btn btn-primary">
                  {t('pages.filesDetailsOpenButton', 'Open file')}
                </a>
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/files')}>
                  {t('pages.filesDetailsBackButton', 'Back to files')}
                </button>
                <button type="button" className="btn btn-ghost" onClick={handleDelete} disabled={deleting}>
                  {t('common.delete', 'Delete')}
                </button>
              </div>
            </div>

            {file.contentType.startsWith('image/') || file.contentType.startsWith('video/') ? (
              <div>
                <p>{t('pages.filesDetailsPreviewLabel', 'Preview')}</p>
                <div style={{ marginTop: '0.75rem' }}>
                  {getPreview(file)}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="alert alert-error">{t('pages.filesDetailsNotFound', 'File not found.')}</div>
      )}
    </div>
  );
};

export default FileDetailsPage;
